"use client";

import type { RefObject, MouseEvent, TouchEvent } from "react";
import { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import TooltipRectangle from "@/icons/ui/tooltip-rectangle.svg";
import { cva } from "class-variance-authority";
import { cn } from "~/lib/utils";
import type { TooltipProps } from "./types";
import { useTooltip } from "./useTooltip";
import { DEFAULT_HIDE_DELAY } from "./constants";
import { useTrackEvent } from "~/hooks/useTrackEvent";

const tooltipVariants = cva(
    "fixed z-[9999] max-w-[320px] p-4 text-sm rounded-2xl select-text transition-opacity duration-150",
    {
        variants: {
            variant: {
                dark: "bg-gray500 text-white",
                light: "bg-pureWhite text-trueBlack",
                secondary: "bg-primary500 text-white",
                primary: "bg-primary100 text-primary500",
            },
        },
        defaultVariants: {
            variant: "dark",
        },
    }
);

const arrowVariants = cva("absolute w-[32px] h-[15px]", {
    variants: {
        variant: {
            dark: "text-gray500",
            light: "text-pureWhite",
            secondary: "text-primary500",
            primary: "text-primary100",
        },
    },
    defaultVariants: {
        variant: "dark",
    },
});

export function Tooltip({
    content,
    position = "top-center",
    children,
    className,
    showDelay = 0,
    hideDelay = DEFAULT_HIDE_DELAY,
    eventData,
    variant = "dark",
    sheet = false,
}: TooltipProps) {
    const sendEvent = useTrackEvent();

    const handleTooltipShow = useCallback(() => {
        if (eventData) {
            sendEvent(eventData);
        }
    }, [eventData, sendEvent]);

    const {
        isOpen,
        isMobile,
        position: actualPosition,
        coords,
        triggerRef,
        tooltipRef,
        tooltipId,
        show,
        hide,
        toggle,
    } = useTooltip({ position, showDelay, hideDelay, sheet, onShow: handleTooltipShow });

    const [render, setRender] = useState(false);

    useEffect(() => {
        setRender(true);
    }, []);

    const arrowClasses = cn(
        arrowVariants({ variant }),
        actualPosition.startsWith("top") ? "-bottom-[14px]" : "rotate-180 -top-[14px]",
        actualPosition.endsWith("center") && "left-1/2 -translate-x-1/2",
        actualPosition.endsWith("left") && "left-6",
        actualPosition.endsWith("right") && "right-6",
        sheet && "max-md:hidden",
    );

    const handleTriggerClick = (e: MouseEvent) => {
        if (isMobile) {
            e.preventDefault();
            toggle();
        }
        children.props.onClick?.(e);
    };

    const handleTouchEnd = (e: TouchEvent) => {
        if (isMobile) {
            e.preventDefault();
            toggle();
        }
    };

    if (!render) {
        return null
    }

    return (
        <>
            <span
                ref={triggerRef as RefObject<HTMLSpanElement>}
                onMouseEnter={!isMobile ? show : undefined}
                onMouseLeave={!isMobile ? hide : undefined}
                onClick={handleTriggerClick}
                onTouchEnd={handleTouchEnd}
                onFocus={!isMobile ? show : undefined}
                onBlur={!isMobile ? hide : undefined}
                onKeyDown={(e) => {
                    if (e.key === "Escape") hide();
                    children.props.onKeyDown?.(e);
                }}
                aria-describedby={isOpen ? tooltipId : undefined}
                tabIndex={children?.props?.tabIndex ?? 0}
                className="inline-flex"
            >
                {children}
            </span>

            {sheet && createPortal(<div
                aria-hidden={!isOpen}
                className={cn(
                    "fixed inset-0 z-50 bg-trueBlack/25 md:hidden",
                    isOpen ? "opacity-100" : "opacity-0 pointer-events-none",
                )}
            />, document.body)}

            {createPortal(
                <div
                    ref={tooltipRef}
                    id={tooltipId}
                    role="tooltip"
                    aria-hidden={!isOpen}
                    onMouseEnter={show}
                    onMouseLeave={hide}
                    className={cn(
                        tooltipVariants({ variant }),
                        "shadow-[0px_10px_15px_-3px_rgba(0,0,0,0.05),0px_4px_6px_-4px_rgba(0,0,0,0.05)]",
                        isOpen ? "opacity-100" : "opacity-0 pointer-events-none",
                        sheet && "max-md:fixed max-md:z-[50] max-md:left-0 max-md:right-0 max-md:bottom-0 max-md:w-full max-md:max-w-[100%] max-md:p-12 max-md:bg-primary100 max-md:rounded-t-3xl max-md:rounded-b-none",
                        className
                    )}
                    style={coords ? { top: coords.top, left: coords.left } : undefined}
                >
                    {sheet && <div className="w-32 h-[5px] bg-gray300 rounded-lg mx-auto md:hidden relative -top-8" />}
                    {content}
                    <TooltipRectangle className={arrowClasses} aria-hidden="true" />
                </div>,
                document.body
            )}
        </>
    );
}

Tooltip.displayName = "Tooltip";
